import React, { useState } from "react";

export const summaryDocumentKey = document => `${document.templateId}:${document.fileId}`;
export const summaryCompleted = document => document && document.generationStatus === "COMPLETED";

const failureMessages = {
  INVALID_GENERATION_OUTPUT: "The generated response could not be validated. No unsupported summary is shown.",
  PROVIDER_FAILURE: "The summary service could not complete this request.",
  TIMED_OUT: "The summary took too long to complete.",
  RETRIEVAL_FAILED: "The document's indexed evidence could not be loaded.",
  // The record has OCR text, but not yet the AI-prepared text summaries read (the vector app prepares it).
  EMPTY_DOCUMENT_TEXT: "This record's text is not yet prepared for AI summaries. Open it in the viewer to read it.",
  EVIDENCE_UNITS_TOO_LARGE: "The document's indexed text could not be divided into statements that can be summarized.",
  SELECTED_EVIDENCE_REJECTED: "The selected text could not be shown as a supported finding. No unsupported summary is shown.",
};
const failureText = document => failureMessages[document.failureCode] || failureMessages[document.emptyReason] ||
  failureMessages[document.status] || "A grounded summary could not be prepared from the available evidence.";

// Why a completed summary has no findings, when the server states it. Wording only; no status code is shown.
const emptyText = document => ({
  ALL_EVIDENCE_FILTERED: "The examined text holds only form captions, table rows or administrative text, which are not shown as summary findings.",
  NO_RELEVANT_EVIDENCE: "No statement in the examined text was selected as a summary finding.",
  NO_READABLE_FALLBACK_EVIDENCE: "No readable statement was found in the examined text.",
}[document.emptyReason] || "No supported summary findings.");

// A summary that is not, or not wholly, the AI-generated one says so in a sentence: the server states where the
// findings came from (summaryOrigin) and why excerpts were used (fallbackReason). Wording only; no code is shown, and
// nothing of the model's reply or of an internal error ever reaches this text.
const limitedReasons = {
  INVALID_MODEL_OUTPUT: ["The AI-generated summary could not be validated,", "Part of the AI-generated summary could not be validated,"],
  MODEL_TIMED_OUT: ["The AI-generated summary did not finish in time,", "Part of the AI-generated summary did not finish in time,"],
  MODEL_CALL_LIMIT: ["This document was not summarized by AI,", "Part of this document was not summarized by AI,"],
};
export const limitedSummaryText = document => {
  if (!document || !["FALLBACK", "MIXED"].includes(document.summaryOrigin)) return "";
  const whole = document.summaryOrigin === "FALLBACK";
  const reason = limitedReasons[document.fallbackReason] ||
    ["An AI-generated summary is not available for this document,", "An AI-generated summary is not available for part of this document,"];
  return `${reason[whole ? 0 : 1]} ${whole ? "so a limited summary from the document was used." : "so readable excerpts from the document were used for that part."}`;
};

function Finding({ finding, source, document, onView }) {
  const [expanded, setExpanded] = useState(false);
  const text = finding.text || "";
  const long = text.length > 240;
  const same = source && source.quote === text;
  // Explicit excerpts, never paraphrases or replacement claims.
  const preview = long ? text.slice(0, 240).replace(/\s+\S*$/, "") + "…" : text;
  const provenance = source && source.pageAwareProvenance;
  const page = provenance && provenance.physical && provenance.physical.pageNumber;
  const hasPage = Number.isInteger(page) && page > 0;
  // A quote across a page break names every page it crosses (first to last).
  const crossed = hasPage && Array.isArray(provenance.physical.pageNumbers) ? provenance.physical.pageNumbers.filter(p => Number.isInteger(p) && p > 0) : [];
  const pageLabel = crossed.length > 1 ? `Pages ${crossed[0]}–${crossed[crossed.length - 1]}` : `Page ${page}`;
  return <li className="summary-finding">
    {!(expanded && same) && <p className="summary-finding-text">{preview}</p>}
    {source && <React.Fragment>
      <button type="button" className="summary-evidence-toggle" aria-expanded={expanded}
        onClick={() => setExpanded(!expanded)}>{expanded ? "Hide evidence" : "Show evidence"}</button>
      {expanded && <blockquote className="summary-quote">{source.quote}</blockquote>}
    </React.Fragment>}
    <div className="summary-citation">
      {/* A page is shown only where the server gives one; index fields and some text have none, and then nothing is said. */}
      {hasPage && <span>{pageLabel}</span>}
      <button type="button" onClick={() => {
        const target = { templateId: document.templateId, fileId: document.fileId, fileName: document.documentName };
        // The cited page opens where the server proves it; otherwise the document opens at its start.
        if (provenance && Number.isInteger(provenance.viewerPageNumber) && provenance.viewerPageNumber > 0)
          Number.isInteger(provenance.viewerPageCount) && provenance.viewerPageCount > 0
            ? onView(target, provenance.viewerPageNumber, provenance.viewerPageCount) : onView(target, provenance.viewerPageNumber);
        else onView(target);
      }}>Open source</button>
    </div>
  </li>;
}

export default function DocumentSummaries({ documents = [], selected = [], loading, response, error, disabled,
  sessionCount, onSummarize, onCancel, onView, hideToolbar }) {
  const distinct = [...new Map(documents.filter(d => Number.isInteger(d.templateId) && Number.isInteger(d.fileId))
    .map(d => [summaryDocumentKey(d), d])).values()];
  const busy = disabled || loading;
  return <section className="chat-search-document-summaries" aria-label="Document summaries" aria-busy={!!loading}>
    {/* hideToolbar: the Search screen's documents header holds "Summarize selected" instead. */}
    {!hideToolbar && <div className="summary-toolbar">
      <div><h4>Document summaries</h4><p>{selected.length} of 4 selected</p></div>
      <button type="button" disabled={busy || selected.length === 0}
        onClick={() => onSummarize(selected.length === 1 ? "SINGLE_DOCUMENT" : "SELECTED_DOCUMENTS",
          distinct.filter(d => selected.includes(summaryDocumentKey(d))))}>Summarize selected</button>
      {sessionCount > 0 && sessionCount <= 4 && <button type="button" disabled={busy}
        onClick={() => onSummarize("CURRENT_SEARCH_SESSION", [])}>Summarize current results</button>}
    </div>}
    {!selected.length && !response && !hideToolbar && <p className="summary-note">Select up to four documents using the checkboxes beside your search results.</p>}
    {loading && <p role="status">Preparing document summaries… <button type="button" onClick={onCancel}>Cancel</button></p>}
    {error && <p role="alert">{error}</p>}
    {response && <div aria-label="Summary results">
      {response.documents.map(document => {
        const metadata = distinct.find(d => summaryDocumentKey(d) === summaryDocumentKey(document));
        const findings = document.findings || [];
        // A selection the server discarded is a failed summary, never an ordinary empty one.
        const empty = findings.length === 0 && document.generationStatus === "COMPLETED" &&
          document.status === "NO_FINDINGS" && !document.failureCode && document.emptyReason !== "SELECTED_EVIDENCE_REJECTED";
        const failed = findings.length === 0 && !empty;
        const label = document.documentName || (metadata && metadata.fileName) || "Document";
        const retryable = ["FAILED", "TIMED_OUT", "RETRIEVAL_FAILED"].includes(document.status);
        const limited = findings.length > 0 ? limitedSummaryText(document) : "";
        return <article className={`summary-card${failed ? " summary-card-failed" : ""}`} key={summaryDocumentKey(document)}>
          <header><div><h4>{label}</h4>{metadata && metadata.repositoryName && <p className="summary-repository">{metadata.repositoryName}</p>}</div>
            {(empty || failed) && <span className={`summary-status ${failed ? "failed" : "summarized"}`}>{empty ? "No findings" : "Failed"}</span>}</header>
          {empty ? <p className="summary-note">{emptyText(document)}</p> : failed ? <div><h5>Summary unavailable</h5><p>{failureText(document)}</p>
            {retryable && <button type="button" disabled={busy} onClick={() => onSummarize("SINGLE_DOCUMENT", [{
              templateId: document.templateId, fileId: document.fileId, fileName: label }])}>Try this summary again</button>}
          </div> : <React.Fragment>
            <h5>Document Summary</h5>
            {limited && <p className="summary-note summary-limited" role="note">{limited}</p>}
            <ul className="summary-findings">{findings.map(finding => <Finding key={finding.evidenceId}
              finding={finding} source={(document.evidenceReferences || []).find(e => e.evidenceId === finding.evidenceId)}
              document={document} onView={onView} />)}</ul>
          </React.Fragment>}
        </article>;
      })}
      <p className="summary-note">{response.documents.some(document => ["PARTIAL", "DEGRADED"].includes(document.status))
        ? "Some information could not be established from the available document text."
        : "Summaries use the available document text and may not cover every part of the original file."}</p>
    </div>}
  </section>;
}

